import { useEffect, useState } from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@jem-hr/jem-ui";
import { SideNavbar } from "./components/SideNavbar";
import { BranchFilter } from "./components/BranchFilter";
import { MobileTopBar } from "./components/MobileTopBar";
import { DesktopOnlyBanner } from "./components/DesktopOnlyBanner";
import { Announcements } from "./pages/Announcements";
import { ReconCalendar } from "./pages/ReconCalendar";
import { Surveys } from "./pages/Surveys";
import { Employees } from "./pages/Employees";
import { FinancialBenefits } from "./pages/FinancialBenefits";
import { Savings } from "./pages/Savings";
import { TimeAttendance } from "./pages/TimeAttendance";
import { BeneficiaryForms } from "./pages/BeneficiaryForms";
import { Multimessaging } from "./pages/Multimessaging";
import { Reports } from "./pages/Reports";
import { ReportPreview } from "./pages/ReportPreview";
import { IntegrationHealth } from "./pages/IntegrationHealth";
import { SiteConfiguration } from "./pages/SiteConfiguration";
import { SitesAndRegions } from "./pages/SitesAndRegions";

const MOBILE_FRIENDLY_PAGES = new Set(["time-attendance-overview", "reports"]);

function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activePage, setActivePage] = useState("home");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isPhone, setIsPhone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsPhone(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Preview mode: when opened with ?preview=1 (typically in a new tab from the
  // Reports wizard), render the standalone preview document with no app chrome.
  const isPreviewMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("preview") === "1";

  if (isPreviewMode) {
    return <ReportPreview />;
  }

  const handleNavigate = (key: string) => {
    setActivePage(key);
    setMobileNavOpen(false);
  };

  // On phone, the drawer is always full-width; respect collapsed only on desktop.
  const effectiveCollapsed = isPhone ? false : sidebarCollapsed;

  const showDesktopOnlyBanner =
    activePage !== "home" && !MOBILE_FRIENDLY_PAGES.has(activePage);

  return (
    <div className="flex h-screen bg-white">
      <SideNavbar
        activeItem={activePage}
        collapsed={effectiveCollapsed}
        mobileOpen={mobileNavOpen}
        onMobileOpenChange={setMobileNavOpen}
        onCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onNavigate={handleNavigate}
        userName="Username"
        userInitials="CN"
      />

      <main className="flex-1 overflow-auto px-sm sm:px-xl sm:pt-lg">
        <MobileTopBar
          onMenuClick={() => setMobileNavOpen(true)}
          onHomeClick={() => handleNavigate("home")}
        />
        {showDesktopOnlyBanner && <DesktopOnlyBanner />}
        {activePage === "home" && (
          <>
            {/* Header */}
            <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
              Hello, Amber!
            </h1>
            <p className="py-[6px] text-base font-regular leading-24 text-greyscale-text-caption">
              Welcome back to Jem Hub. See what&apos;s new at Jem and review your
              company&apos;s activities.
            </p>

            <div className="sm:hidden mt-md rounded-md border border-primary-navy-200 bg-primary-navy-50 p-sm">
              <p className="text-sm font-semibold text-greyscale-text-title leading-tight">
                Mobile experience - work in progress
              </p>
              <p className="text-xs text-greyscale-text-caption mt-xxxs">
                Time &amp; Attendance is the only feature currently optimised
                for mobile. Other pages are best viewed on a larger screen.
              </p>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="news-feed" className="mt-md hidden sm:block">
              <TabsList variant="line" className="w-full justify-start">
                <TabsTrigger value="news-feed" variant="line">
                  News Feed
                </TabsTrigger>
                <TabsTrigger value="company-activity" variant="line">
                  Company Activity
                </TabsTrigger>
                <TabsTrigger value="integration-health" variant="line">
                  Integration Health
                </TabsTrigger>
              </TabsList>

              <TabsContent value="news-feed">
                <div className="bg-neutral-50 rounded-lg p-lg">

                  {/* Article */}
                  <article className="mx-auto">

                    {/* Video */}
                    <div className="rounded-xxxs overflow-hidden">
                      <iframe
                        src="https://helpdesk.jemhr.online/ebd//26a4331e23ef80e0889ac81e8a7dc181"
                        width="100%"
                        height="600"
                        style={{ border: 0 }}
                        allowFullScreen
                        title="How to set up your new password"
                      />
                    </div>
                  </article>
                </div>
              </TabsContent>

              <TabsContent value="company-activity">
                {/* Filter by branch */}
                <BranchFilter
                  branches={["All Branches", "Jem"]}
                  className="mt-md mb-sm"
                />

                {/* Dashboard */}
                <div className="rounded-lg overflow-auto h-screen">
                  <img
                    src="/dashboard-placeholder.png"
                    alt="Registrations & Engagement Dashboard"
                    className="w-full"
                  />
                </div>
              </TabsContent>

              <TabsContent value="integration-health">
                <IntegrationHealth />
              </TabsContent>
            </Tabs>
          </>
        )}

        {activePage === "recons" && <ReconCalendar />}
        {activePage === "announcements" && <Announcements />}
        {activePage === "surveys" && <Surveys />}
        {activePage === "employees" && <Employees />}
        {activePage === "financial-benefits" && <FinancialBenefits />}
        {activePage === "savings" && <Savings />}
        {activePage === "time-attendance-overview" && (
          <TimeAttendance onNavigate={handleNavigate} />
        )}
        {activePage === "reports" && <Reports />}
        {activePage === "site-configuration" && (
          <SitesAndRegions
            onNavigateToSite={() => handleNavigate("site-configuration-detail")}
          />
        )}
        {activePage === "site-configuration-detail" && (
          <SiteConfiguration onNavigate={handleNavigate} />
        )}
        {activePage === "beneficiary-forms" && <BeneficiaryForms />}
        {activePage === "multimessaging" && <Multimessaging />}
      </main>
    </div>
  );
}

export default App;
